import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { AddFresherCandidateResponse } from '@edtech-ui/core/models/company-management/add-fresher-data';

@Injectable({ providedIn: 'root' })
export class CandidateApiService {
  private apiUrl = environment.candidateApiUrl;

  constructor(private http: HttpClient) { }

  private get companyId(): string {
    return localStorage.getItem('company_id') || '';
  }

  listCandidates(filters: any): Observable<any> {
    let params = new HttpParams().set('company_id', this.companyId);
    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value);
      }
    });
    return this.http.get<any>(this.apiUrl, { params });
  }

  getCandidate(candidateId: string): Observable<any> {
    const params = new HttpParams().set('company_id', this.companyId);
    return this.http.get<any>(`${this.apiUrl}/${candidateId}`, { params });
  }

  getStats(): Observable<any> {
    const params = new HttpParams().set('company_id', this.companyId);
    return this.http.get<any>(`${this.apiUrl}/stats`, { params });
  }

  addFresher(fd: FormData): Observable<any> {
    fd.set('company_id', this.companyId);
    return this.http.post<any>(`${this.apiUrl}/fresher`, fd);
  }

  addExperienced(fd: FormData): Observable<any> {
    fd.set('company_id', this.companyId);
    return this.http.post<any>(`${this.apiUrl}/experienced`, fd);
  }

  addEducator(fd: FormData): Observable<any> {
    fd.set('company_id', this.companyId);
    return this.http.post<any>(`${this.apiUrl}/educator`, fd);
  }

  bulkImportCsv(type: string, file: File): Observable<any> {
    const fd = new FormData();
    fd.append('file', file);
    const params = new HttpParams()
      .set('company_id', this.companyId)
      .set('candidate_type', type);
    return this.http.post<any>(`${this.apiUrl}/csv-upload`, fd, { params });
  }

  updateCandidate(candidateId: string, payload: any): Observable<any> {
    const params = new HttpParams().set('company_id', this.companyId);
    return this.http.put<any>(`${this.apiUrl}/${candidateId}`, payload, { params });
  }

  deleteCandidate(candidateId: string): Observable<any> {
    const params = new HttpParams().set('company_id', this.companyId);
    return this.http.delete<any>(`${this.apiUrl}/${candidateId}`, { params });
  }

  changeStatus(candidateId: string, status: string, jobId?: string): Observable<any> {
    let params = new HttpParams().set('company_id', this.companyId);
    if (jobId) {
      params = params.set('job_id', jobId);
    }
    return this.http.post<any>(`${this.apiUrl}/${candidateId}/status`, { status }, { params });
  }

  addComment(candidateId: string, comment: string): Observable<any> {
    const params = new HttpParams().set('company_id', this.companyId);
    return this.http.post<any>(`${this.apiUrl}/${candidateId}/comment`, { comment }, { params });
  }

  assignAssessment(candidateId: string, companyId?: string, jobId?: string, body?: any): Observable<any> {
    const compId = companyId || this.companyId;
    let params = new HttpParams().set('company_id', compId);
    if (jobId) {
      params = params.set('job_id', jobId);
    }
    return this.http.post(`${this.apiUrl}/${candidateId}/assessment`, body, { params });
  }

  assignAiInterview(candidateId: string, roundId: string, companyId?: string, jobId?: string): Observable<any> {
    const compId = companyId || this.companyId;
    let params = new HttpParams().set('company_id', compId);
    if (jobId) {
      params = params.set('job_id', jobId);
    }
    const body = {
      screening_round_id: roundId,
      send_invitation: true
    };
    return this.http.post<any>(`${this.apiUrl}/${candidateId}/ai-interview`, body, { params });
  }

  exportCandidates(type?: string): Observable<Blob> {
    let params = new HttpParams().set('company_id', this.companyId);
    if (type) params = params.set('candidate_type', type);
    return this.http.get(`${this.apiUrl}/export`, { params, responseType: 'blob' });
  }


  postFresherAddCandidate(payload: FormData): Observable<AddFresherCandidateResponse> {
    return this.http.post<AddFresherCandidateResponse>(`${this.apiUrl}/fresher`, payload);
  }


  postExperiencedAddCandidate(payload: FormData): Observable<AddFresherCandidateResponse> {
    return this.http.post<AddFresherCandidateResponse>(`${this.apiUrl}/experienced`, payload);
  }


  postEducatorAddCandidate(payload: FormData): Observable<AddFresherCandidateResponse> {
    return this.http.post<AddFresherCandidateResponse>(`${this.apiUrl}/educator`, payload);
  }


  deleteCandidateAssignment(candidateId: string, assessmentId: string): Observable<any> {
    const params = new HttpParams().set('company_id', this.companyId);
    return this.http.delete<any>(`${this.apiUrl}/${candidateId}/assessments/${assessmentId}`, { params });
  }


  deleteCandidateInterview(candidateId: string, assessmentId: string): Observable<any> {
    const params = new HttpParams().set('company_id', this.companyId);
    return this.http.delete<any>(`${this.apiUrl}/${candidateId}/ai-interviews/${assessmentId}`, { params });
  }

  assignJobToCandidate(candidateId: string, jobId: string): Observable<any> {
    const params = new HttpParams().set('company_id', this.companyId);
    return this.http.post<any>(`${this.apiUrl}/${candidateId}/jobs`, { job_id: jobId }, { params });
  }

  updateJobFinalStatus(candidateId: string, jobId: string, finalStatus: string, comment?: string): Observable<any> {
    const params = new HttpParams().set('company_id', this.companyId);
    return this.http.post<any>(`${this.apiUrl}/${candidateId}/jobs/${jobId}/final-status`, { final_status: finalStatus, comment }, { params });
  }

  exportCandidateTemplate(companyId: string, candidateType: string) {
    const params = new HttpParams().set('company_id', companyId).set('candidate_type', candidateType);
    return this.http.get(`${this.apiUrl}/export`, { params, responseType: 'blob' })
  }

}
